// TODO: write tests as soon as vite-test is available in the project
import { DateTime } from 'luxon';
import { generateLink } from '@/utils/helpers/generateLink';
import { getLocale } from '@/utils/i18n/vueI18n';

export type Point = [number, number];
export type PointPair = [Point, Point];
export type Point2D = { x: number; y: number };
export type BoundingBox = {
  x: number;
  y: number;
  x2: number;
  y2: number;
  width: number;
  height: number;
};
export type BoundingRect = {
  height: number;
  width: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
};
export type Override<T, R> = Omit<T, keyof R> & R;

/**
 * @name clamp
 * @description
 * Returns a value between an upper and a lower bound
 *
 * @param value
 * @param min
 * @param max
 * @returns
 */
export function clamp(value: number, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}

export type Tuple = [number, number];
export type TupleDouble = [number, number, number, number];
export type TupleTriple = [number, number, number, number, number, number];
export function toPrecision(
  precision: number,
  data: number[],
): number[] | Tuple | TupleDouble | TupleTriple {
  return data.map((v) => Number.parseFloat(v.toFixed(precision))) as number[];
}

/**
 * @name constant
 * @description
 * Creates a closure which returns the given parameter
 *
 * @param x
 * @returns {Function}
 */
export function constant<T>(x: T): () => T {
  return () => x;
}

/**
 * @name rad2deg
 * @description
 * Geometry helper to convert radiant to degree
 *
 * @param angle
 * @returns
 */
export function rad2deg(angle: number) {
  return (angle * 180) / Math.PI;
}

/**
 * @name deg2rad
 * @description
 * Gemetry helper to convert degree to radiant
 *
 * @param angle
 * @returns
 */
export function deg2rad(angle: number) {
  return (angle * Math.PI) / 180;
}

/**
 * @name distance2d
 * @description
 * Geometry helper to calculate the distance between two points
 *
 * @param pointA
 * @param pointB
 * @returns
 */
export function distance2d(pointA: Point, pointB: Point) {
  return Math.hypot(pointA[0] - pointB[0], pointA[1] - pointB[1]);
}

export const jwt = {
  decodeBase64Url: (base64url: string): string => {
    let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) {
      base64 += '='.repeat(4 - pad);
    }
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));

    return new TextDecoder().decode(bytes);
  },
  decode: <T>(token: string) => {
    if (!jwt.empty(token)) {
      const parts = token.split('.');
      if (parts.length < 2) {
        console.error(
          '[jwt.decode] Invalid token format (expected 3 parts, got ' +
            parts.length +
            '). Token starts with:',
          token.substring(0, 20) + '...',
        );

        return { payload: { exp: -1 } as T };
      }
      try {
        const [headerRaw, payloadRaw] = parts;
        const [header, payload] = [headerRaw, payloadRaw].map(
          (part: string) => {
            const jsonFragment = jwt.decodeBase64Url(part);

            return JSON.parse(jsonFragment);
          },
        );

        return { header, payload: payload as T };
      } catch (error) {
        console.error('[jwt.decode] Failed to decode token:', error);

        return { payload: { exp: -1 } as T };
      }
    } else {
      return { payload: { exp: -1 } };
    }
  },
  empty: (token: string) => {
    const check = token ?? '';

    return check === '';
  },
  expired: (token: string) => {
    if (jwt.empty(token)) {
      return true;
    }
    const { payload } = jwt.decode<{ exp: number }>(token);

    return Date.now() < payload.exp * 1000;
  },
};

export function useSelectable<T extends { id: number | string }>() {
  const selected = new Set<T['id']>();
  const actions = {
    select: (id: T['id']) => selected.add(id),
    unselect: (id: T['id']) => selected.delete(id),
    clear: () => selected.clear(),
  };
  const getters = {
    hasSelection: () => selected.size > 0,
    isSelected: (id: T['id']) => selected.has(id),
  };

  return {
    state: { selected },
    getters,
    actions,
  };
}
/**
 * Keeps a value strictly positive or zero
 *
 * In other words, ensure that the value is "not negative".
 * Especially useful to set attributes in SVG where negative values are not allowed
 * and therefore should be minimum 0.
 */
export const min0 = Math.max.bind(Math, 0);

export function cancelBubble(event: Event) {
  event.cancelBubble = true;
}

export function openUrl(url?: string | null, target = '_blank') {
  if (!url) {
    return;
  }
  const link = document.createElement('a');
  link.target = target;
  url = generateLink(url);
  link.href = url;
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function abbreviateNumber(
  num: number,
  ranges = [
    { divider: 1e18, suffix: 'E' },
    { divider: 1e15, suffix: 'P' },
    { divider: 1e12, suffix: 'T' },
    { divider: 1e9, suffix: 'B' },
    { divider: 1e6, suffix: 'M' },
    { divider: 1e3, suffix: 'K' },
  ],
) {
  for (let i = 0; i < ranges.length; i++) {
    if (num >= ranges[i].divider) {
      const decimals = num % ranges[i].divider ? 2 : 0;

      return (num / ranges[i].divider).toFixed(decimals) + ranges[i].suffix;
    }
  }

  return num.toString();
}

export function formatDateString(date: string) {
  return date.split('T')[0].split('-').reverse().join('.');
}

export const formatDateShortMonthNoYear: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  timeZone: 'utc',
};

export const formatDateShortMonth: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: 'utc',
};
// Same as formatDateShortMonth but renders in the viewer's local timezone
// instead of UTC. Use for real point-in-time values (e.g. meeting dates) where
// the calendar day should reflect the user's local day, avoiding a near-midnight
// off-by-one. The UTC variant remains correct for midnight-UTC calendar dates.
export const formatDateShortMonthLocal: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

export const formatDateNumeric: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour12: false,
  timeZone: 'utc',
};
// Same as formatDateNumeric but renders in the viewer's local timezone instead
// of UTC. Use for real point-in-time values (e.g. meeting dates) where the
// calendar day should reflect the user's local day, avoiding a near-midnight
// off-by-one. The UTC variant remains correct for midnight-UTC calendar dates.
export const formatDateNumericLocal: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour12: false,
};
export const formatDateTimeNumeric: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
};

export function formatDate(
  date: Date | string,
  options = formatDateShortMonth,
  locale = getLocale(),
): string {
  const parsedDate = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, options).format(parsedDate);
}

export function parseDate(value: unknown): Date | null {
  if (typeof value === 'string') {
    const date = new Date(value);

    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

export function daysFromNow(toDate: Date | string) {
  return Math.ceil(
    (new Date(toDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24),
  );
}

export function toRelativeTime(
  isoString: string,
  locale = getLocale(),
): string {
  try {
    const dateTime = DateTime.fromISO(isoString).setLocale(locale);
    const diffInMinutes = Math.abs(dateTime.diffNow('minutes').minutes);

    // If less than 1 minute, show "1 minute ago" instead of seconds
    if (diffInMinutes < 1) {
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

      return rtf.format(-1, 'minute');
    }

    return dateTime.toRelative() || '-';
  } catch {
    return '-';
  }
}

/**
 * Number of days after which a timestamp switches from a relative label
 * ("3h ago", "2d ago") to an absolute local date. Beyond a week the relative
 * phrasing loses precision.
 */
export const SMART_TIME_RELATIVE_THRESHOLD_DAYS = 7;

export interface SmartTime {
  /** Short label for inline display: relative when recent, otherwise a local date. */
  label: string;
  /** Full local date + time, intended for a hover `title` tooltip. */
  tooltip: string;
}

/**
 * "Smart" timestamp formatting for an ISO 8601 string:
 *  - younger than {@link SMART_TIME_RELATIVE_THRESHOLD_DAYS} days → relative
 *    time (instant-based, timezone-safe).
 *  - older → absolute date only, rendered in the user's LOCAL timezone (year
 *    shown only for prior years).
 *  - `tooltip` is always the full local date + time.
 *
 * The absolute path deliberately uses Luxon's local-zone `toLocaleString`
 * rather than the `formatDate*` constants in this file, which carry
 * `timeZone: 'utc'` and would be off by a day near midnight for non-UTC users.
 */
export function toSmartTime(
  isoString: string,
  locale = getLocale(),
): SmartTime {
  const fallback: SmartTime = { label: '-', tooltip: '' };

  const dateTime = DateTime.fromISO(isoString).setLocale(locale);
  if (!dateTime.isValid) {
    return fallback;
  }

  const tooltip = dateTime.toLocaleString({
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const ageInDays = DateTime.now().diff(dateTime, 'days').days;
  if (ageInDays < SMART_TIME_RELATIVE_THRESHOLD_DAYS) {
    return { label: toRelativeTime(isoString, locale), tooltip };
  }

  const isPriorYear = dateTime.year < DateTime.now().year;
  const label = dateTime.toLocaleString({
    day: 'numeric',
    month: 'short',
    ...(isPriorYear ? { year: 'numeric' } : {}),
  });

  return { label, tooltip };
}

export function toPercentage(
  value: number,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
) {
  return new Intl.NumberFormat(getLocale(), {
    style: 'percent',
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
}

/**
 * Wraps all occurrences of `term` in the given `text` with `<mark>` tags.
 * Returns `text` unchanged when `term` is empty.
 */
export function highlightSearchTerm(
  text: string,
  term: string | undefined,
): string {
  const trimmed = term?.trim();
  if (!trimmed) {
    return text;
  }
  const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
}

/**
 * Shortens a string to a maximum length, adding ellipsis if needed
 * @param str - The string to shorten
 * @param maxLength - The maximum length (default: 255)
 * @returns The shortened string with ellipsis if truncated
 */
export function shortenString(str: string, maxLength = 255): string {
  if (str.length <= maxLength) {
    return str;
  }
  const trimmedLength = maxLength - 3;

  return str.substring(0, trimmedLength) + '...';
}

export function isFirefox() {
  return navigator.userAgent.search('Firefox') > -1;
}

/**
 * @name joinMultiselectValues
 * @description
 * Normalizes a multiselect custom-property value (a `string[]`) into a stable,
 * display-ready string by sorting its values alphabetically (case-insensitive)
 * before joining them with ", ". This guarantees that two items sharing the
 * same set of values group, sort, and render identically regardless of the
 * order in which the values happen to be stored — e.g. both
 * `['DACH', 'Nordics']` and `['Nordics', 'DACH']` become `"DACH, Nordics"`.
 *
 * Use this at every site where a multiselect value array is joined (group key,
 * group header, row cells, column sort) so they all stay consistent.
 *
 * Non-array / empty values return `''`.
 */
/** Comparable value of a table cell; `null` means "no value" (sorts last). */
export type SortValue = string | number | null;

/**
 * Sort comparator for table columns whose cells hold mixed value types
 * (WI #5641). Empty cells always sort last, so flipping the direction never
 * buries the rows that do have a value. Text compares case- and
 * accent-insensitively.
 */
export function compareSortValues(
  a: SortValue,
  b: SortValue,
  ascending: boolean,
): number {
  if (a === null || b === null) {
    return a === b ? 0 : a === null ? 1 : -1;
  }

  const direction = ascending ? 1 : -1;

  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * direction;
  }

  return (
    String(a).localeCompare(String(b), undefined, { sensitivity: 'base' }) *
    direction
  );
}

export function joinMultiselectValues(value: unknown): string {
  if (!Array.isArray(value)) {
    return '';
  }

  return [...value]
    .filter((item) => item != null && item !== '')
    .map((item) => String(item))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .join(', ');
}

// calculate the delta between two objects
// TODO we loose typing here, not sure how to add it
export function diff(
  first: Record<string, unknown>,
  second: Record<string, unknown>,
  treatEmptyAsNull = false,
) {
  const o1 = JSON.parse(JSON.stringify(first));
  const o2 = JSON.parse(JSON.stringify(second));

  return Object.keys(o2).reduce((diff, key) => {
    if (Array.isArray(o1[key])) {
      if (JSON.stringify(o1[key]) == JSON.stringify(o2[key])) {
        return diff;
      }
    } else if (JSON.stringify(o1[key]) == JSON.stringify(o2[key])) {
      return diff;
    }

    return {
      ...diff,
      [key]: second[key] || (treatEmptyAsNull ? null : second[key]),
    };
  }, {});
}
